import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import * as Sentry from '@sentry/react';
import { logout, getAccessToken } from './auth';

const base_URL = process.env.REACT_APP_BASE_URL || 'http://localhost:8000';

const axiosInstance = axios.create({
    baseURL: base_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// ── Global loading state handlers ────────────────────────────────────────────
// Registered once from App.js via registerLoadingHandlers().
// Incremented on every request, decremented on every response or error.
let _incrementLoading = null;
let _decrementLoading = null;

export const registerLoadingHandlers = (increment, decrement) => {
    _incrementLoading = increment;
    _decrementLoading = decrement;
};

// ── Shared refresh state ─────────────────────────────────────────────────────
// Used by BOTH interceptors below, so a request-interceptor refresh and a
// response-interceptor refresh never race each other into refreshing twice.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

const doRefresh = async () => {
    const response = await axios.post(`${base_URL}/api/token/refresh/`, {
        refresh: localStorage.getItem('refreshToken'),
    });
    const { access, refresh } = response.data;
    localStorage.setItem('accessToken', access);
    localStorage.setItem('refreshToken', refresh);
    return access;
};

const forceLogout = () => {
    logout();
    window.location.href = '/login';
};

// ── Request interceptor ──────────────────────────────────────────────────────
// Proactive check: if the token we're about to send is already expired
// (per its own exp claim), refresh before the request goes out at all.
axiosInstance.interceptors.request.use(
    async (config) => {
        // Only writes block the screen; GETs (lists, lookups, background polls) leave it usable.
        config._blocking = ['post', 'put', 'patch', 'delete'].includes((config.method || 'get').toLowerCase());
        if (config._blocking) _incrementLoading?.();
        const token = getAccessToken();

        if (token) {
            const decodedToken = jwtDecode(token);
            const isExpired = decodedToken.exp * 1000 < Date.now();

            if (isExpired) {
                if (!isRefreshing) {
                    isRefreshing = true;
                    try {
                        const access = await doRefresh();
                        processQueue(null, access);
                        config.headers['Authorization'] = `Bearer ${access}`;
                        return config;
                    } catch (err) {
                        processQueue(err, null);
                        Sentry.captureException(err, {
                            tags: { auth_flow: 'proactive_refresh' },
                        });
                        forceLogout();
                        return Promise.reject(err);
                    } finally {
                        isRefreshing = false;
                    }
                }

                return new Promise((resolve, reject) => {
                    failedQueue.push({
                        resolve: (token) => {
                            config.headers['Authorization'] = `Bearer ${token}`;
                            resolve(config);
                        },
                        reject: (err) => reject(err),
                    });
                });
            } else {
                config.headers['Authorization'] = `Bearer ${token}`;
            }
        }

        return config;
    },
    (error) => {
        if (error?.config?._blocking) _decrementLoading?.();
        return Promise.reject(error);
    }
);

// ── Response interceptor ─────────────────────────────────────────────────────

axiosInstance.interceptors.response.use(
    (response) => {
        if (response.config?._blocking) _decrementLoading?.();
        return response;
    },
    async (error) => {
        if (error.config?._blocking) _decrementLoading?.();

        const originalRequest = error.config;
        const status = error.response?.status;
        const errorCode = error.response?.data?.code || error.response?.data?.error_detail;
        const isAuthError = status === 401 || errorCode === 'token_not_valid';

        // Genuine network failure (server unreachable, CORS, timeout, DNS) 
        if (!error.response) {
            Sentry.captureException(error, {
                tags: { auth_flow: 'network_error' },
                extra: { url: originalRequest?.url, method: originalRequest?.method },
            });
            return Promise.reject(error);
        }

        // Don't retry the refresh call itself, and only ever retry once per request
        if (!isAuthError || originalRequest?._retry || originalRequest?.url?.includes('/token/refresh/')) {
            return Promise.reject(error);
        }

        originalRequest._retry = true;

        if (!localStorage.getItem('refreshToken')) {
            forceLogout();
            return Promise.reject(error);
        }

        if (!isRefreshing) {
            isRefreshing = true;
            try {
                const access = await doRefresh();
                processQueue(null, access);
                originalRequest.headers['Authorization'] = `Bearer ${access}`;
                return axiosInstance(originalRequest);
            } catch (refreshErr) {
                processQueue(refreshErr, null);
                Sentry.captureException(refreshErr, {
                    tags: { auth_flow: 'reactive_refresh' },
                    extra: { url: originalRequest?.url },
                });
                forceLogout();
                return Promise.reject(refreshErr);
            } finally {
                isRefreshing = false;
            }
        }

        // A refresh triggered by another request is already in flight — queue
        // this one and retry it once that refresh resolves.
        return new Promise((resolve, reject) => {
            failedQueue.push({
                resolve: (token) => {
                    originalRequest.headers['Authorization'] = `Bearer ${token}`;
                    resolve(axiosInstance(originalRequest));
                },
                reject: (err) => reject(err),
            });
        });
    }
);

export default axiosInstance;